/**
 * 用户偏好管理 Hook
 *
 * 提供用户偏好设置的读写能力:
 * - 支持 localStorage 缓存 (离线可用)
 * - 支持可选的 API 同步 (云端持久化)
 * - 自动合并默认值 (浅合并)
 * - 防抖同步
 *
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiResponse } from '../types/api';
import { useLatestRef } from './useLatestRef';

// ===== 类型定义 =====

/**
 * useUserPreference 配置
 */
export interface UseUserPreferenceOptions<T extends Record<string, unknown>> {
  /** 偏好设置 key (localStorage 和 API 共用) */
  key: string;
  /** 默认值 */
  defaultValue: T;
  /** localStorage 缓存 key 前缀, 默认 'user-pref-' */
  storagePrefix?: string;
  /** 是否启用 localStorage 缓存, 默认 true */
  enableCache?: boolean;
  /** 读取 API (可选, 不传则仅使用 localStorage) */
  fetchApi?: (key: string) => Promise<ApiResponse<T>>;
  /** 保存 API (可选) */
  saveApi?: (key: string, value: T) => Promise<ApiResponse<unknown>>;
  /** API 成功状态码, 默认 '200' */
  successCode?: string | number;
  /** 保存防抖延迟 (ms), 默认 500 */
  debounceMs?: number;
  /** 值变化回调 */
  onChange?: (value: T) => void;
}

/**
 * useUserPreference 返回值
 */
export interface UseUserPreferenceReturn<T> {
  /** 当前偏好值 */
  value: T;
  /** 设置偏好值 */
  setValue: (value: T | ((prev: T) => T)) => void;
  /** 重置为默认值 */
  reset: () => void;
  /** 是否正在加载 */
  loading: boolean;
  /** 是否正在保存 */
  saving: boolean;
  /** 手动同步到服务器 */
  sync: () => Promise<void>;
}

// ===== Hook 实现 =====

/**
 * 用户偏好管理 Hook
 *
 * @example
 * ```tsx
 * // 纯本地偏好
 * const { value: theme, setValue: setTheme } = useUserPreference({
 *   key: 'theme',
 *   defaultValue: { mode: 'light', primaryColor: '#1677ff' },
 * });
 *
 * // 带 API 同步
 * const { value: layout, setValue: setLayout, loading } = useUserPreference({
 *   key: 'layout',
 *   defaultValue: { sidebarCollapsed: false, contentWidth: 'fluid' },
 *   fetchApi: (key) => getUserPreference(key),
 *   saveApi: (key, value) => saveUserPreference(key, value),
 * });
 *
 * // 使用
 * <Switch checked={!layout.sidebarCollapsed} onChange={(checked) =>
 *   setLayout(prev => ({ ...prev, sidebarCollapsed: !checked }))
 * } />
 * ```
 */
export function useUserPreference<T extends Record<string, unknown>>(
  options: UseUserPreferenceOptions<T>,
): UseUserPreferenceReturn<T> {
  const {
    key,
    defaultValue,
    storagePrefix = 'user-pref-',
    enableCache = true,
    fetchApi,
    saveApi,
    successCode = '200',
    debounceMs = 500,
  } = options;

  const storageKey = `${storagePrefix}${key}`;

  // 稳定引用
  const defaultValueRef = useLatestRef(defaultValue);
  const onChangeRef = useLatestRef(options.onChange);
  const saveApiRef = useLatestRef(saveApi);
  const fetchApiRef = useLatestRef(fetchApi);

  // 安全访问 localStorage（SSR 保护）
  const canUseStorage = typeof window !== 'undefined';

  // 初始值: 尝试从缓存读取
  const getInitialValue = (): T => {
    if (!enableCache || !canUseStorage) return defaultValue;
    try {
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        return { ...defaultValue, ...JSON.parse(cached) };
      }
    } catch {
      // 解析失败, 使用默认值
    }
    return defaultValue;
  };

  const [value, setValueState] = useState<T>(getInitialValue);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // 防抖 timer
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // 缓存到 localStorage
  const cacheToStorage = useCallback(
    (val: T) => {
      if (!enableCache || !canUseStorage) return;
      try {
        localStorage.setItem(storageKey, JSON.stringify(val));
      } catch {
        // 存储满了或不可用
      }
    },
    [enableCache, canUseStorage, storageKey],
  );

  // 保存到 API
  const saveToApi = useCallback(
    async (val: T) => {
      if (!saveApiRef.current) return;
      setSaving(true);
      try {
        await saveApiRef.current(key, val);
      } catch {
        // 保存失败静默处理
      } finally {
        setSaving(false);
      }
    },
    [key, saveApiRef],
  );

  // 纯 setValue — 无副作用
  const setValue = useCallback((newValue: T | ((prev: T) => T)) => {
    setValueState((prev) =>
      typeof newValue === 'function'
        ? (newValue as (prev: T) => T)(prev)
        : newValue,
    );
  }, []);

  // 副作用: value 变化时执行缓存、通知、防抖同步
  const isInitialMount = useRef(true);
  useEffect(() => {
    // 跳过首次渲染 (初始值不需要触发副作用)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    cacheToStorage(value);
    onChangeRef.current?.(value);

    if (saveApiRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        saveToApi(value);
      }, debounceMs);
    }
  }, [value, cacheToStorage, saveToApi, debounceMs, onChangeRef, saveApiRef]);

  // 重置
  const reset = useCallback(() => {
    setValue(defaultValueRef.current);
  }, [setValue, defaultValueRef]);

  // 手动同步
  const sync = useCallback(async () => {
    await saveToApi(value);
  }, [saveToApi, value]);

  // 首次加载: 从 API 获取（仅在 key 变化时重新请求）
  useEffect(() => {
    if (!fetchApiRef.current) return;

    let cancelled = false;
    setLoading(true);

    fetchApiRef
      .current(key)
      .then((response) => {
        if (cancelled) return;
        const isSuccess = String(response.code) === String(successCode);
        if (isSuccess && response.data) {
          const merged = {
            ...defaultValueRef.current,
            ...(response.data as T),
          };
          setValueState(merged);
          cacheToStorage(merged);
        }
      })
      .catch(() => {
        // 加载失败, 使用缓存值
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [key, successCode, cacheToStorage, defaultValueRef, fetchApiRef]);

  // 清理防抖
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  return {
    value,
    setValue,
    reset,
    loading,
    saving,
    sync,
  };
}
